import { redirectToLogin } from "./loginNavigation"

const ACCESS_TOKEN_KEY = "fiscalhub_access_token"
const REFRESH_TOKEN_KEY = "fiscalhub_refresh_token"

let loginRedirectStarted = false

export const redirectToLoginOnUnauthorized = () => {
  if (loginRedirectStarted) return
  if (window.location.pathname.startsWith("/login")) return
  loginRedirectStarted = true
  clearSession()
  const returnPath = `${window.location.pathname}${window.location.search}`
  redirectToLogin(returnPath.startsWith("/") ? returnPath : "/app")
}

export const getAccessToken = () => sessionStorage.getItem(ACCESS_TOKEN_KEY)

export const getRefreshToken = () => sessionStorage.getItem(REFRESH_TOKEN_KEY)

export const setSessionTokens = (accessToken: string, refreshToken?: string) => {
  sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  if (refreshToken) {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }
}

export const clearSession = () => {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY)
  sessionStorage.removeItem(REFRESH_TOKEN_KEY)
}

export const hasActiveSession = () => Boolean(getAccessToken())

export type LocalLoginResponse = {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  usuario?: {
    must_change_password?: boolean
  }
}

const formatApiErrorDetail = (detail: unknown): string => {
  if (typeof detail === "string") {
    return detail
  }
  if (detail && typeof detail === "object" && "message" in detail) {
    return String((detail as { message: string }).message)
  }
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (item && typeof item === "object" && "msg" in item) {
          return String((item as { msg: string }).msg)
        }
        return JSON.stringify(item)
      })
      .join("; ")
  }
  return "Falha no login"
}

export const loginLocal = async (email: string, password: string): Promise<LocalLoginResponse> => {
  const { config } = await import("./config")
  const base = config.apiUrl
  let response: Response
  try {
    response = await fetch(`${base}/v1/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
    })
  } catch {
    throw new Error("Não foi possível contactar a API. Verifique se ela está em execução.")
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new Error(formatApiErrorDetail(body.detail))
  }
  return response.json()
}

export const requestPasswordReset = async (email: string): Promise<{ ok: boolean; message: string }> => {
  const { config } = await import("./config")
  const response = await fetch(`${config.apiUrl}/v1/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new Error(formatApiErrorDetail(body.detail))
  }
  return response.json()
}

export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  const { config } = await import("./config")
  const token = getAccessToken()
  const response = await fetch(`${config.apiUrl}/v1/auth/change-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new Error(formatApiErrorDetail(body.detail))
  }
}
